<script setup lang="ts">
import { ref, watch } from 'vue';
import type { ProductWithDetails } from '../../types';
import { useProductStore } from '../../stores';
import { useNotificationStore } from '../../stores/notificationStore';
import ProductForm from './ProductForm.vue';
import Modal from '../Modal.vue';

const props = defineProps<{
  show: boolean;
  product: ProductWithDetails | null;
}>();

const emit = defineEmits(['close', 'saved']);

const productStore = useProductStore();
const notificationStore = useNotificationStore();
const saving = ref(false);
const error = ref('');

// Watch for changes in the product prop to properly initialize the form
watch(() => props.product, (newProduct) => {
  if (newProduct) {
    // Find the complete product data from the store
    const completeProduct = productStore.products.find(p => p.id === newProduct.id);
    if (completeProduct) {
      // Create a deep copy to avoid modifying the store directly
      const productCopy = JSON.parse(JSON.stringify(completeProduct));
      props.product.laboratories = productCopy.laboratories;
    }
  }
}, { immediate: true });

const handleSave = async (updatedProduct: ProductWithDetails) => {
  saving.value = true;
  error.value = '';
  
  try {
    if (props.product?.id) {
      await productStore.updateProduct(props.product.id, updatedProduct);
      notificationStore.success('Producto actualizado correctamente');
    } else {
      await productStore.addProduct(updatedProduct);
      notificationStore.success('Producto creado correctamente');
    }
    emit('saved');
    emit('close');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Error al guardar el producto';
    notificationStore.error(errorMessage);
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <Modal
    :show="show"
    :title="product ? 'Editar Producto' : 'Nuevo Producto'"
    @close="emit('close')"
  >
    <ProductForm
      :initial-data="product"
      :saving="saving"
      @submit="handleSave"
      @cancel="emit('close')"
    />
  </Modal>
</template>